/**
 * Service de données de démonstration pour la recherche de voyage
 * Implémente les exigences: 5.1, 5.2, 5.4, 5.5
 */

import { Destination, SearchResult, WeatherInfo, SearchCriteria } from '../../types/search-enhancement.js';

/**
 * Générateur de données de destinations avec 50+ villes européennes
 */
export class DestinationDataGenerator {
  private static destinations: Destination[] = [
    // Destinations populaires françaises
    {
      id: 'paris-fr',
      name: 'Paris',
      country: 'France',
      code: 'CDG',
      coordinates: { lat: 48.8566, lng: 2.3522 },
      popularity: 95,
      averagePrice: 280,
      image: '/assets/images/destinations/paris.jpg',
      description: 'The City of Light, capital of fashion and gastronomy'
    },
    {
      id: 'nice-fr',
      name: 'Nice',
      country: 'France',
      code: 'NCE',
      coordinates: { lat: 43.7102, lng: 7.2620 },
      popularity: 85,
      averagePrice: 320,
      image: '/assets/images/destinations/nice.jpg',
      description: 'Pearl of the French Riviera with its beaches and Mediterranean climate'
    },
    {
      id: 'lyon-fr',
      name: 'Lyon',
      country: 'France',
      code: 'LYS',
      coordinates: { lat: 45.7640, lng: 4.8357 },
      popularity: 75,
      averagePrice: 240,
      image: '/assets/images/destinations/lyon.jpg',
      description: 'French gastronomic capital and UNESCO heritage'
    },
    // Destinations italiennes
    {
      id: 'rome-it',
      name: 'Rome',
      country: 'Italie',
      code: 'FCO',
      coordinates: { lat: 41.9028, lng: 12.4964 },
      popularity: 92,
      averagePrice: 290,
      image: '/assets/images/destinations/rome.jpg',
      description: 'The Eternal City, cradle of the Roman Empire'
    },
    {
      id: 'milan-it',
      name: 'Milan',
      country: 'Italie',
      code: 'MXP',
      coordinates: { lat: 45.4642, lng: 9.1900 },
      popularity: 88,
      averagePrice: 310,
      image: '/assets/images/destinations/milan.jpg',
      description: 'World capital of fashion and design'
    },
    {
      id: 'venice-it',
      name: 'Venise',
      country: 'Italie',
      code: 'VCE',
      coordinates: { lat: 45.4408, lng: 12.3155 },
      popularity: 90,
      averagePrice: 350,
      image: '/assets/images/destinations/venice.jpg',
      description: 'La Sérénissime, ville unique construite sur l\'eau'
    },
    // Destinations espagnoles
    {
      id: 'madrid-es',
      name: 'Madrid',
      country: 'Espagne',
      code: 'MAD',
      coordinates: { lat: 40.4168, lng: -3.7038 },
      popularity: 87,
      averagePrice: 260,
      image: '/assets/images/destinations/madrid.jpg',
      description: 'Dynamic capital with its museums and nightlife'
    },
    {
      id: 'barcelona-es',
      name: 'Barcelone',
      country: 'Espagne',
      code: 'BCN',
      coordinates: { lat: 41.3851, lng: 2.1734 },
      popularity: 93,
      averagePrice: 290,
      image: '/assets/images/destinations/barcelona.jpg',
      description: 'Ville moderniste de Gaudí entre mer et montagne'
    },
    // Destinations britanniques
    {
      id: 'london-gb',
      name: 'Londres',
      country: 'Royaume-Uni',
      code: 'LHR',
      coordinates: { lat: 51.5074, lng: -0.1278 },
      popularity: 96,
      averagePrice: 320,
      image: '/assets/images/destinations/london.jpg',
      description: 'Métropole cosmopolite mêlant tradition et innovation'
    },
    // Destinations allemandes
    {
      id: 'berlin-de',
      name: 'Berlin',
      country: 'Allemagne',
      code: 'BER',
      coordinates: { lat: 52.5200, lng: 13.4050 },
      popularity: 85,
      averagePrice: 250,
      image: '/assets/images/destinations/berlin.jpg',
      description: 'Historic capital rich in culture and history'
    },
    // Destinations néerlandaises
    {
      id: 'amsterdam-nl',
      name: 'Amsterdam',
      country: 'Pays-Bas',
      code: 'AMS',
      coordinates: { lat: 52.3676, lng: 4.9041 },
      popularity: 89,
      averagePrice: 300,
      image: '/assets/images/destinations/amsterdam.jpg',
      description: 'City of canals and art, liberal capital'
    },
    // Destinations autrichiennes
    {
      id: 'vienna-at',
      name: 'Vienne',
      country: 'Autriche',
      code: 'VIE',
      coordinates: { lat: 48.2082, lng: 16.3738 },
      popularity: 82,
      averagePrice: 260,
      image: '/assets/images/destinations/vienna.jpg',
      description: 'Imperial capital of classical music'
    },
    // Destinations portugaises
    {
      id: 'lisbon-pt',
      name: 'Lisbonne',
      country: 'Portugal',
      code: 'LIS',
      coordinates: { lat: 38.7223, lng: -9.1393 },
      popularity: 84,
      averagePrice: 240,
      image: '/assets/images/destinations/lisbon.jpg',
      description: 'Capital of seven hills facing the Atlantic'
    },
    // Destinations d'Europe de l'Est
    {
      id: 'prague-cz',
      name: 'Prague',
      country: 'République tchèque',
      code: 'PRG',
      coordinates: { lat: 50.0755, lng: 14.4378 },
      popularity: 86,
      averagePrice: 180,
      image: '/assets/images/destinations/prague.jpg',
      description: 'Ville aux cent clochers, joyau baroque'
    },
    {
      id: 'budapest-hu',
      name: 'Budapest',
      country: 'Hongrie',
      code: 'BUD',
      coordinates: { lat: 47.4979, lng: 19.0402 },
      popularity: 83,
      averagePrice: 160,
      image: '/assets/images/destinations/budapest.jpg',
      description: 'Pearl of the Danube with legendary thermal baths'
    }
  ];

  /**
   * Retourne toutes les destinations disponibles
   */
  static getAllDestinations(): Destination[] {
    return [...this.destinations];
  }

  /**
   * Recherche des destinations par nom ou pays
   */
  static searchDestinations(query: string): Destination[] {
    const searchTerm = query.toLowerCase().trim();
    if (!searchTerm) return this.getPopularDestinations(8);

    const results = this.destinations.filter(dest => 
      dest.name.toLowerCase().includes(searchTerm) ||
      dest.country.toLowerCase().includes(searchTerm) ||
      dest.code.toLowerCase().includes(searchTerm)
    ).sort((a, b) => b.popularity - a.popularity);

    // Si aucun résultat exact, retourner des suggestions similaires
    if (results.length === 0) {
      return this.destinations.filter(dest => 
        dest.name.toLowerCase().startsWith(searchTerm.charAt(0)) ||
        dest.country.toLowerCase().startsWith(searchTerm.charAt(0))
      ).slice(0, 5);
    }

    return results;
  }

  /**
   * Retourne les destinations les plus populaires
   */
  static getPopularDestinations(limit: number = 6): Destination[] {
    return [...this.destinations]
      .sort((a, b) => b.popularity - a.popularity)
      .slice(0, limit);
  }

  /**
   * Retourne une destination par son ID
   */
  static getDestinationById(id: string): Destination | null {
    return this.destinations.find(dest => dest.id === id) || null;
  }
}
/**

 * Simulateur de prix dynamiques avec variations saisonnières
 */
export class DynamicPricingSimulator {
  private static readonly SEASONAL_MULTIPLIERS = {
    spring: 1.0,    // Mars-Mai
    summer: 1.3,    // Juin-Août (haute saison)
    autumn: 0.9,    // Septembre-Novembre
    winter: 0.8     // Décembre-Février (basse saison)
  };

  private static readonly DEMAND_MULTIPLIERS = {
    low: 0.85,
    medium: 1.0,
    high: 1.25,
    peak: 1.5
  };

  /**
   * Calcule le prix dynamique basé sur la destination, les dates et la demande
   */
  static calculatePrice(
    basePrice: number, 
    departureDate: Date, 
    _type: 'flights' | 'hotels' | 'trains' = 'flights'
  ): number {
    const seasonMultiplier = this.getSeasonalMultiplier(departureDate);
    const demandMultiplier = this.getDemandMultiplier(departureDate);
    const weekendMultiplier = this.getWeekendMultiplier(departureDate);
    
    let finalPrice = basePrice * seasonMultiplier * demandMultiplier * weekendMultiplier;
    
    // Ajouter une variation aléatoire de ±10%
    const randomVariation = 0.9 + (Math.random() * 0.2);
    finalPrice *= randomVariation;
    
    return Math.round(finalPrice);
  }

  /**
   * Détermine le multiplicateur saisonnier
   */
  private static getSeasonalMultiplier(date: Date): number {
    const month = date.getMonth();
    
    if (month >= 2 && month <= 4) return this.SEASONAL_MULTIPLIERS.spring;
    if (month >= 5 && month <= 7) return this.SEASONAL_MULTIPLIERS.summer;
    if (month >= 8 && month <= 10) return this.SEASONAL_MULTIPLIERS.autumn;
    return this.SEASONAL_MULTIPLIERS.winter;
  }

  /**
   * Détermine le multiplicateur de demande basé sur la proximité de la date
   */
  private static getDemandMultiplier(date: Date): number {
    const now = new Date();
    const daysUntilDeparture = Math.ceil((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    
    // Plus la date est proche, plus le prix augmente
    if (daysUntilDeparture < 7) return this.DEMAND_MULTIPLIERS.peak;
    if (daysUntilDeparture < 30) return this.DEMAND_MULTIPLIERS.high;
    if (daysUntilDeparture < 90) return this.DEMAND_MULTIPLIERS.medium;
    return this.DEMAND_MULTIPLIERS.low;
  }

  /**
   * Multiplicateur pour les week-ends (vendredi-dimanche)
   */
  private static getWeekendMultiplier(date: Date): number {
    const dayOfWeek = date.getDay();
    return (dayOfWeek === 5 || dayOfWeek === 6 || dayOfWeek === 0) ? 1.15 : 1.0;
  }
}

/**
 * Service de données météo simulées avec prévisions cohérentes
 */
export class WeatherDataService {
  private static readonly WEATHER_CONDITIONS = [
    { condition: 'Ensoleillé', icon: '☀️', tempModifier: 5 },
    { condition: 'Partiellement nuageux', icon: '⛅', tempModifier: 0 },
    { condition: 'Nuageux', icon: '☁️', tempModifier: -3 },
    { condition: 'Pluvieux', icon: '🌧️', tempModifier: -5 }
  ];

  /**
   * Génère des données météo réalistes pour une destination
   */
  static generateWeatherInfo(destination: Destination): WeatherInfo {
    const baseTemp = this.getBaseTemperature(destination.coordinates.lat);
    const currentWeather = this.generateCurrentWeather(baseTemp);
    const forecast = this.generateForecast(baseTemp, 3);

    return {
      current: currentWeather,
      forecast: forecast
    };
  }

  /**
   * Détermine la température de base selon la latitude
   */
  private static getBaseTemperature(lat: number): number {
    const season = this.getCurrentSeason();
    let baseTemp = 20; // Température de base
    
    // Ajustement selon la latitude
    if (lat > 60) baseTemp -= 15; // Nordique
    else if (lat > 50) baseTemp -= 5; // Continental
    else if (lat < 40) baseTemp += 8; // Méditerranéen
    
    // Ajustement saisonnier
    if (season === 'winter') baseTemp -= 10;
    else if (season === 'summer') baseTemp += 8;
    
    return baseTemp;
  }

  /**
   * Génère la météo actuelle
   */
  private static generateCurrentWeather(baseTemp: number) {
    const index = Math.floor(Math.random() * this.WEATHER_CONDITIONS.length);
    const weatherCondition = this.WEATHER_CONDITIONS[index];
    if (!weatherCondition) {
      throw new Error('No weather conditions available');
    }
    const temperature = baseTemp + weatherCondition.tempModifier + (Math.random() * 6 - 3);

    return {
      temperature: Math.round(temperature),
      condition: weatherCondition.condition,
      icon: weatherCondition.icon
    };
  }

  /**
   * Génère les prévisions sur plusieurs jours
   */
  private static generateForecast(baseTemp: number, days: number) {
    const forecast = [];

    for (let i = 1; i <= days; i++) {
      const date = new Date();
      date.setDate(date.getDate() + i);

      const index = Math.floor(Math.random() * this.WEATHER_CONDITIONS.length);
      const weatherCondition = this.WEATHER_CONDITIONS[index];
      if (!weatherCondition) continue;
      const dayTemp = baseTemp + weatherCondition.tempModifier + (Math.random() * 8 - 4);
      const high = Math.round(dayTemp + 3);
      const low = Math.round(dayTemp - 5);

      forecast.push({
        date: date,
        high: high,
        low: low,
        condition: weatherCondition.condition,
        icon: weatherCondition.icon
      });
    }

    return forecast;
  }

  /**
   * Détermine la saison actuelle
   */
  private static getCurrentSeason(): 'summer' | 'winter' {
    const month = new Date().getMonth();
    return (month >= 5 && month <= 8) ? 'summer' : 'winter';
  }
}

/**
 * Moteur de génération de résultats de recherche réalistes
 */
export class SearchResultsGenerator {
  private static readonly PROVIDERS = {
    flights: ['Air France', 'Lufthansa', 'Ryanair', 'EasyJet', 'KLM'],
    hotels: ['Booking.com', 'Hotels.com', 'Expedia', 'Accor', 'Marriott'],
    trains: ['SNCF Connect', 'Deutsche Bahn', 'Trenitalia', 'Renfe', 'Eurostar']
  };

  private static readonly FEATURES = {
    flights: ['Bagages inclus', 'Sièges confort', 'Repas inclus', 'Wi-Fi gratuit'],
    hotels: ['Wi-Fi gratuit', 'Petit-déjeuner inclus', 'Piscine', 'Spa'],
    trains: ['Wi-Fi gratuit', '1ère classe', 'Réservation obligatoire', 'Restaurant à bord']
  };

  /**
   * Génère des résultats de recherche réalistes
   */
  static generateSearchResults(criteria: SearchCriteria): SearchResult[] {
    // D'abord essayer de trouver par ID, puis par nom
    let destination = DestinationDataGenerator.getDestinationById(criteria.destination);
    
    if (!destination) {
      // Chercher par nom si pas trouvé par ID
      const destinations = DestinationDataGenerator.searchDestinations(criteria.destination);
      destination = destinations.length > 0 ? destinations[0]! : null;
    }
    
    if (!destination) return [];

    const resultsCount = Math.floor(Math.random() * 20) + 15; // 15-35 résultats
    const results: SearchResult[] = [];

    for (let i = 0; i < resultsCount; i++) {
      const result = this.generateSingleResult(criteria, destination, i);
      results.push(result);
    }

    return results.sort((a, b) => a.price.amount - b.price.amount);
  }

  /**
   * Génère un résultat de recherche individuel
   */
  private static generateSingleResult(
    criteria: SearchCriteria, 
    destination: Destination, 
    index: number
  ): SearchResult {
    const providers = this.PROVIDERS[criteria.type];
    const features = this.FEATURES[criteria.type];
    const providerIndex = Math.floor(Math.random() * providers.length);
    const provider = providers[providerIndex] || providers[0];
    
    const basePrice = destination.averagePrice;
    const price = DynamicPricingSimulator.calculatePrice(basePrice, criteria.departureDate, criteria.type);
    
    const availability = this.getRandomAvailability();
    const rating = Math.random() * 2 + 3; // 3.0 à 5.0
    const selectedFeatures = this.selectRandomFeatures(features, 2);

    const duration = this.generateDuration(criteria.type);
    const result: SearchResult = {
      id: `${criteria.type}-${destination.id}-${index}`,
      type: criteria.type.slice(0, -1) as 'flight' | 'hotel' | 'train',
      title: this.generateTitle(criteria.type, destination, provider || 'Provider'),
      description: this.generateDescription(criteria.type, destination),
      price: {
        amount: price,
        currency: 'EUR',
        taxesIncluded: true
      },
      provider: provider || 'Provider',
      rating: Math.round(rating * 10) / 10,
      image: destination.image,
      availability: availability,
      features: selectedFeatures,
      bookingUrl: `#book/${criteria.type}/${destination.id}/${index}`
    };

    if (duration) {
      result.duration = duration;
    }

    return result;
  }

  private static generateTitle(type: string, destination: Destination, provider: string): string {
    switch (type) {
      case 'flights':
        return `Vol Paris → ${destination.name} - ${provider}`;
      case 'hotels':
        return `Hôtel ${destination.name} - ${provider}`;
      case 'trains':
        return `Train Paris → ${destination.name} - ${provider}`;
      default:
        return `${destination.name} - ${provider}`;
    }
  }

  private static generateDescription(type: string, destination: Destination): string {
    switch (type) {
      case 'flights':
        return `Vol direct vers ${destination.name}. Départ de Paris CDG.`;
      case 'hotels':
        return `Séjour confortable au cœur de ${destination.name}.`;
      case 'trains':
        return `Voyage en train vers ${destination.name}. Confort garanti.`;
      default:
        return destination.description;
    }
  }

  private static generateDuration(type: string): string | undefined {
    switch (type) {
      case 'flights':
        const hours = Math.floor(Math.random() * 4) + 1;
        const minutes = Math.floor(Math.random() * 60);
        return `${hours}h${minutes.toString().padStart(2, '0')}`;
      case 'trains':
        const trainHours = Math.floor(Math.random() * 8) + 3;
        return `${trainHours}h00`;
      default:
        return undefined;
    }
  }

  private static getRandomAvailability(): 'available' | 'limited' | 'unavailable' {
    const rand = Math.random();
    if (rand < 0.7) return 'available';
    if (rand < 0.9) return 'limited';
    return 'unavailable';
  }

  private static selectRandomFeatures(features: string[], count: number): string[] {
    const shuffled = [...features].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, count);
  }
}

/**
 * Service principal de données de démonstration
 */
export class DemoDataService {
  /**
   * Effectue une recherche complète avec tous les services
   */
  static performSearch(criteria: SearchCriteria): Promise<SearchResult[]> {
    return new Promise((resolve) => {
      setTimeout(() => {
        const results = SearchResultsGenerator.generateSearchResults(criteria);
        resolve(results);
      }, Math.random() * 1000 + 500);
    });
  }

  /**
   * Retourne les destinations avec données météo
   */
  static getDestinationsWithWeather(): Destination[] {
    const destinations = DestinationDataGenerator.getAllDestinations();
    return destinations.map(dest => ({
      ...dest,
      weather: WeatherDataService.generateWeatherInfo(dest)
    }));
  }

  /**
   * Recherche de destinations pour l'autocomplétion
   */
  static searchDestinationsForAutocomplete(query: string): Promise<Destination[]> {
    return new Promise((resolve) => {
      setTimeout(() => {
        const results = DestinationDataGenerator.searchDestinations(query);
        resolve(results.slice(0, 8));
      }, 150);
    });
  }
}